import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { formatDate } from "@utils/helperFunction";
import { JobType, skillsUsed } from "@utils/constants";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import {
  getJobAverageResponseTime,
  getJobById,
  jobSeen,
} from "@endpoints/job.service";
import { jobMatchesSkills } from "@endpoints/candidate.service";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import { useSelector } from "react-redux";

const MIN_SAMPLE_SIZE = 5;

const formatResponseTime = (stats) => {
  if (!stats) return null;
  const { averageResponseTimeMinutes: m } = stats;
  if (m == null) return null;
  if (m < 60) return { value: "< 1", unit: "hour" };
  if (m < 24 * 60) {
    const h = Math.round(m / 60);
    return { value: h, unit: h === 1 ? "hour" : "hours" };
  }
  if (m < 7 * 24 * 60) {
    const d = Math.round(m / (24 * 60));
    return { value: d, unit: d === 1 ? "day" : "days" };
  }
  return { value: "7+", unit: "days" };
};

const getResponsivenessTier = (stats) => {
  if (!stats || stats.totalApplications < MIN_SAMPLE_SIZE) return null;
  const rate = stats.respondedApplications / stats.totalApplications;
  const hours = stats.averageResponseTimeHours;
  if (hours < 24 && rate >= 0.8) {
    return {
      label: "Highly responsive",
      dot: "bg-emerald-500",
      pill: "bg-emerald-50 text-emerald-700 border-emerald-100",
      bar: "bg-emerald-500",
    };
  }
  if (hours < 72 && rate >= 0.7) {
    return {
      label: "Responsive",
      dot: "bg-sky-500",
      pill: "bg-sky-50 text-sky-700 border-sky-100",
      bar: "bg-sky-500",
    };
  }
  if (hours > 120 || rate < 0.5) {
    return {
      label: "Slow to respond",
      dot: "bg-amber-500",
      pill: "bg-amber-50 text-amber-700 border-amber-100",
      bar: "bg-amber-500",
    };
  }
  return {
    label: "Typical",
    dot: "bg-slate-400",
    pill: "bg-slate-100 text-slate-600 border-slate-200",
    bar: "bg-slate-400",
  };
};

const ClockIcon = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export const RecruiterResponsiveness = ({ stats }) => {
  if (!stats) return null;
  if (stats.totalApplications < MIN_SAMPLE_SIZE) {
    return (
      <aside className="h-max w-[280px] rounded-lg border border-[#ECECEC] bg-white px-5 py-4">
        <div className="flex items-center gap-2 text-slate-500">
          <ClockIcon className="w-4 h-4" />
          <span className="text-xs font-medium">Response insights</span>
        </div>
        <p className="mt-3 text-xs text-slate-400 leading-relaxed">
          Not enough application history yet to estimate this recruiter's
          response time.
        </p>
      </aside>
    );
  }

  const tier = getResponsivenessTier(stats);
  const time = formatResponseTime(stats);
  const rate = Math.round(
    (stats.respondedApplications / stats.totalApplications) * 100,
  );

  return (
    <aside className="h-max w-[280px] rounded-lg border border-[#ECECEC] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5 text-slate-500">
          <ClockIcon className="w-3.5 h-3.5" />
          <span className="text-xss font-semibold uppercase tracking-wide">
            Recruiter response
          </span>
        </div>
        {tier && (
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border w-max ${tier.pill}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${tier.dot}`} />
            {tier.label}
          </span>
        )}
      </header>

      <div className="mt-4 flex items-baseline gap-1.5">
        <span className="text-3xl font-semibold text-slate-900 leading-none">
          {time?.value}
        </span>
        <span className="text-sm font-medium text-slate-500">{time?.unit}</span>
      </div>
      <p className="mt-1 text-[11px] text-slate-400">
        Average time to first reply
      </p>

      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-medium text-slate-600">
            Response rate
          </span>
          <span className="text-[11px] font-semibold text-slate-900">
            {rate}%
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full rounded-full ${tier?.bar ?? "bg-slate-400"}`}
            style={{ width: `${rate}%` }}
          />
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          {stats.respondedApplications} of {stats.totalApplications} applicants
          received a reply
        </p>
      </div>
    </aside>
  );
};

const tabs = [
  {
    title: "View Job",
    value: "View Job",
  },

  {
    title: "Skills Match",
    value: "Skills Match",
  },
];

const JobMatchedDetailPage = () => {
  const location = useLocation();
  const { item } = location.state || {};
  const jobId = item?.jobId;
  const navigate = useNavigate();
  const loggedInUserId = useSelector((state) => state.auth.user?._id);
  const candidateId =
    item?.candidateId || item?.candidateDetails?.candidateId || loggedInUserId;

  // Local state
  const [loading, setLoading] = useState(true);
  const [jobDetails, setJobDetails] = useState();
  const [averageResponseTime, setAverageResponseTime] = useState();
  const [matchSkills, setMatchSkills] = useState();
  const [activeTab, setActiveTab] = useState("description");
  const [activeDetailTab, setActiveDetailTab] = useState("View Job");
  const [isJobApplied, setIsJobApplied] = useState(false);
  const [showReferralDetails, setShowReferralDetails] = useState(false);

  const job = JobType.find((x) => x.value === jobDetails?.jobType)?.label;
  const isReferredJob = jobDetails?.referredStatus === "referred";

  const fetchJob = async () => {
    try {
      const { success, data } = await getJobById({ jobId, candidateId });
      if (success) {
        const { data: responseTimeData } = await getJobAverageResponseTime({
          recruiterId: data?.createdBy,
        });
        setAverageResponseTime(responseTimeData);
        setIsJobApplied(data?.hasApplied);
        setJobDetails(data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobMatcheSkills = async () => {
    try {
      if (!candidateId || !jobId) return;
      const { data, success } = await jobMatchesSkills(candidateId, jobId);

      if (success) {
        setMatchSkills(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (candidateId && jobId) {
      fetchJobMatcheSkills();
    }
    if (jobId && candidateId) {
      jobSeen({ jobId: jobId });
      fetchJob();
    }
  }, [candidateId, jobId]);

  return (
    <div className="flex bg-white flex-col rounded-lg w-full gap-7 p-6 ">
      <button
        onClick={() => navigate("/job/job-matches", { replace: true })}
        className="flex items-center self-start"
      >
        <ChevronLeftIcon className={"w-4 h-4"} fill="#4d4d4d" />
        <label className="text-xs font-medium text-charcoal cursor-pointer">
          Back to listing
        </label>
      </button>

      <div className="flex items-center border-b border-gray-300 gap-16 pb-2 pl-4 ">
        {tabs?.map((tab) => {
          const isActive = tab?.value === activeDetailTab;
          return (
            <button
              key={tab?.value}
              onClick={() => setActiveDetailTab(tab?.value)}
              className={`flex items-center ${
                isActive
                  ? "border-b border-primary"
                  : "border-b border-transparent"
              }`}
            >
              <span
                className={`text-xs pb-0.5 ${
                  isActive ? "text-charcoal font-medium" : "text-gray-500"
                }`}
              >
                {tab?.title}
              </span>
            </button>
          );
        })}
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <LoadingAnimation color={"black"} />
        </div>
      ) : (
        <>
          {activeDetailTab === "View Job" ? (
            <div className="flex gap-5">
              <div className="flex flex-1 flex-col max-h-fit overflow-y-scroll no-scrollbar border border-[##DADCE0] px-6 py-10 rounded-md gap-3.5 w-[60%] ">
                <div className="flex justify-between items-center mb-12 gap-4">
                  <p className="font-medium text-xs text-light-black italic">
                    Posted{" "}
                    <span className="pl-3">
                      {formatDate(jobDetails?.createdAt)}
                    </span>
                  </p>
                  {!isJobApplied ? (
                    <div className="flex gap-2">
                      <button
                        className="flex justify-center font-medium text-xs text-white border-[#4D4D4D] rounded-lg bg-[#6F6F6F] px-8 py-2 items-center gap-2"
                        onClick={() => navigate(`/apply-job/${jobId}`)}
                      >
                        Apply
                      </button>

                      <button
                        className={`flex justify-center font-medium bg-[#F1F5F9] text-xs text-charcoal rounded-lg border border-[#ECECEC] px-8 py-2 items-center gap-2 ${
                          !isReferredJob ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                        onClick={() => {
                          if (!isReferredJob) return;
                          setShowReferralDetails((prev) => !prev);
                        }}
                        disabled={!isReferredJob}
                        title={
                          isReferredJob
                            ? "View referral details"
                            : "This job was not referred"
                        }
                      >
                        Refer
                      </button>

                      <button className="flex justify-center font-medium bg-[#F1F5F9] text-xs text-charcoal rounded-lg border border-[#ECECEC] px-8 py-2 items-center gap-2">
                        Track
                      </button>
                    </div>
                  ) : null}
                </div>

                {isReferredJob && showReferralDetails && (
                  <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <span className="text-xs font-semibold text-green-800">
                        Referral Details
                      </span>
                      <span className="text-[10px] font-semibold px-3 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 whitespace-nowrap">
                        Referred
                      </span>
                    </div>
                    <div className="grid gap-2">
                      <div className="flex gap-3">
                        <span className="text-xs text-light-black w-28">
                          Referral By:
                        </span>
                        <span className="text-xs font-medium text-charcoal">
                          {jobDetails?.referredByReferralName || "N/A"}
                        </span>
                      </div>
                      <div className="flex gap-3">
                        <span className="text-xs text-light-black w-28">
                          Referral Ref:
                        </span>
                        <span className="text-xs font-medium text-charcoal">
                          {jobDetails?.matchRefNo || "N/A"}
                        </span>
                      </div>
                      <div className="flex gap-3">
                        <span className="text-xs text-light-black w-28">
                          Referred At:
                        </span>
                        <span className="text-xs font-medium text-charcoal">
                          {jobDetails?.referredAt
                            ? formatDate(jobDetails?.referredAt)
                            : "N/A"}
                        </span>
                      </div>
                      <div className="flex gap-3">
                        <span className="text-xs text-light-black w-28">
                          Status:
                        </span>
                        <span className="text-xs font-medium text-charcoal capitalize">
                          {jobDetails?.referredStatus || "referred"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col flex-1 gap-6">
                  <div className="flex justify-between items-center ">
                    <p className="font-semibold text-charcoal text-xl">
                      {jobDetails?.title}
                    </p>
                    <span className="font-medium text-light-black text-xs">
                      {jobDetails?.refNo}
                    </span>
                  </div>

                  <div className="flex flex-col gap-2 mb-6">
                    <div className="flex gap-5">
                      <span className="text-xs text-light-black w-28">
                        Agency:
                      </span>
                      <span className="font-medium text-charcoal text-xs flex-1">
                        {/* {selectedJob?.companyDetails?.name ?? "N/A"} */}
                      </span>
                    </div>
                    <div className="flex gap-5">
                      <span className="text-xs text-light-black w-28">
                        Location:
                      </span>
                      <span className="font-medium text-charcoal text-xs flex-1">
                        {jobDetails?.location}
                      </span>
                    </div>
                    <div className="flex gap-5">
                      <span className="text-xs text-light-black w-28">
                        Industry Type:
                      </span>
                      <span className="font-medium text-charcoal text-xs flex-1">
                        {jobDetails?.industryType}
                      </span>
                    </div>
                    <div className="flex gap-5">
                      <span className="text-xs text-light-black w-28">
                        Job Type:
                      </span>
                      <span className="font-medium text-charcoal text-xs flex-1">
                        {job}
                      </span>
                    </div>
                    <div className="flex gap-5">
                      <span className="text-xs text-light-black w-28">
                        Salary Range:
                      </span>
                      <span className="font-medium text-charcoal text-xs flex-1">
                        £{jobDetails?.salary.min} - £{jobDetails?.salary.max}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex space-x-12 mb-6 mt-3 bg-[#F1F5F9] rounded p-4">
                    <button
                      onClick={() => setActiveTab("description")}
                      className={`py-1 px-4 text-xs font-semibold ${
                        activeTab === "description"
                          ? "border-b border-[#2E2E2E] text-charcoal"
                          : "text-[#6f6f6f] border-b border-transparent"
                      }`}
                    >
                      Job Description
                    </button>
                    <button
                      onClick={() => setActiveTab("skills")}
                      className={`py-1 px-4 text-xs font-semibold ${
                        activeTab === "skills"
                          ? "border-b border-[#2E2E2E] text-charcoal"
                          : "text-[#6f6f6f] border-b-2 border-transparent"
                      }`}
                    >
                      Skills Required
                    </button>
                  </div>

                  {activeTab === "description" && (
                    <div className="my-3">
                      <span className="font-semibold text-charcoal text-sm">
                        Job description
                      </span>
                      <p
                        dangerouslySetInnerHTML={{
                          __html: jobDetails?.description,
                        }}
                        className="text-light-black text-xs mt-2"
                      />
                    </div>
                  )}

                  {activeTab === "skills" && jobDetails?.skills?.length > 0 && (
                    <div className="w-3/4">
                      <div className="grid flex-1 mt-7 mb-2 justify-center grid-cols-[1.9fr_0.75fr_0.6fr_1fr]">
                        {["Required skill", "Rating", "Years", "When"].map(
                          (label) => (
                            <span
                              key={label}
                              className="text-xs font-semibold text-gray-700 flex-1"
                            >
                              {label}
                            </span>
                          ),
                        )}
                      </div>
                      <div className="overflow-auto h-full">
                        {jobDetails?.skills?.map((skill) => {
                          const lastUsed = Object.entries(skillsUsed).find(
                            ([key, value]) => value === skill.lastUsed,
                          );
                          return (
                            <div
                              key={skill?._id}
                              className="grid space-x-3 py-2.5 items-center w-full justify-center relative grid-cols-[1.7fr_0.68fr_0.58fr_1fr]"
                            >
                              {[
                                skill.title,
                                `${skill.rating}/10`,
                                skill.years,
                                lastUsed ? lastUsed[0] : "N/A",
                              ].map((value, index) => (
                                <div
                                  key={index}
                                  className="border border-[#F0F0F0] bg-[#FAFAFA] px-4 py-2.5 text-xs font-medium text-charcoal rounded flex flex-1"
                                >
                                  {value}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <RecruiterResponsiveness stats={averageResponseTime} />
            </div>
          ) : (
            <>
              {matchSkills?.jobMatchesDetails?.map((match, index) => (
                <div key={index} className="flex items-center w-full gap-2">
                  <div className="flex items-center py-4 w-[45%] px-4 text-xs font-semibold bg-[#F1F5F9] border-[#ECECEC] rounded-sm">
                    Your skill set
                  </div>

                  <span className="text-xs px-2 w-[10%] border-b pb-2 font-medium italic text-light-black ">
                    {match?.totalMatchPercentage?.toFixed(2)}% matched
                  </span>

                  <div className="flex justify-between items-center w-[45%] py-4 px-4 bg-[#F1F5F9] gap-4 border-[#ECECEC] rounded-sm">
                    <span className="font-medium text-charcoal text-xs">
                      {match?.jobDetails?.title}
                    </span>
                    <span className="font-normal text-charcoal text-xs">
                      {match?.jobDetails?.refNo}
                    </span>
                  </div>
                </div>
              ))}

              <div className="flex flex-col gap-2.5">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="grid grid-cols-[1fr,0.25fr,0.25fr,1fr] gap-3 w-[80%]">
                      {["Skill", "Rating", "Years", "When"].map((label) => (
                        <span
                          key={`candidate-${label}`}
                          className="text-xs font-semibold text-gray-700"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                    <div className="w-[20%]" />
                    <div className="grid grid-cols-[1fr,0.25fr,0.25fr,1fr] gap-3 w-[80%]">
                      {["Skill", "Rating", "Years", "When"].map((label) => (
                        <span
                          key={`job-${label}`}
                          className="text-xs font-semibold text-gray-700"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  {matchSkills?.skillsMatchesDetails?.map((skill, index) => {
                    const getLastUsedLabel = (value) =>
                      Object.entries(skillsUsed).find(
                        ([_, v]) => v === value,
                      )?.[0] || "N/A";

                    const candidateSkillData = [
                      skill?.candidateSkill?.title,
                      `${skill?.candidateSkill?.rating}/10`,
                      skill?.candidateSkill?.years,
                      getLastUsedLabel(skill?.jobSkill?.lastUsed),
                    ];

                    const requiredSkillData = [
                      skill?.jobSkill?.title,
                      `${skill?.jobSkill?.rating}/10`,
                      skill?.jobSkill?.years,
                      getLastUsedLabel(skill?.candidateSkill?.lastUsed),
                    ];

                    return (
                      <div
                        key={index}
                        className="flex items-center w-full justify-between gap-4"
                      >
                        <div className="grid grid-cols-[1fr,0.25fr,0.25fr,1fr] gap-2 w-[82%]">
                          {candidateSkillData.map((value, idx) => (
                            <div
                              key={idx}
                              className="border border-[#F0F0F0] bg-[#FAFAFA] px-4 py-2.5 text-xs font-medium text-charcoal rounded"
                            >
                              {value}
                            </div>
                          ))}
                        </div>
                        <div className="w-[16%] flex items-center justify-center">
                          <span className="text-[10px] border-b pb-2 font-medium italic text-light-black">
                            {skill?.matchingPercentage?.toFixed(2)}% matched
                          </span>
                        </div>

                        <div className="grid grid-cols-[1fr,0.25fr,0.25fr,1fr] gap-2 w-[82%]">
                          {requiredSkillData.map((value, idx) => (
                            <div
                              key={idx}
                              className="border border-[#F0F0F0] bg-[#FAFAFA] px-4 py-2.5 text-xs font-medium text-charcoal rounded"
                            >
                              {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default JobMatchedDetailPage;
